import { useCallback, useEffect, useRef } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { atomWithGlobalState } from '@highlight/ipc/jotai'
import type { TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'

/**
 * A paywall the sidepanel home was asked to dock before its meter reads as
 * spent, such as a low-credits nag's "Add credits" or a gate refusal that lands
 * before the billing refresh.
 */
export interface SidepanelPaywallRequest {
  /** The top-up to sell. */
  kind: TopUpKind
  /** When it was made, so a later billing refresh can retire it. See resolveTopUpStrips. */
  requestedAt: number
}

/**
 * GlobalState-backed, not a local atom: the chat, settings and meeting-note
 * windows are separate renderers, and a request made in one has to reach the
 * sidepanel in another. This is what lets those windows sell an upgrade without
 * a modal of their own.
 */
const sidepanelPaywallRequestAtom = atomWithGlobalState<SidepanelPaywallRequest | null>(
  'sidepanel.paywallRequest',
  null,
)

export const useSidepanelPaywallRequest = () => useAtomValue(sidepanelPaywallRequestAtom)

/** Drops the request for one kind; another kind's request stands. */
export function useClearSidepanelPaywallRequest() {
  const set = useSetAtom(sidepanelPaywallRequestAtom)
  return useCallback((kind: TopUpKind) => set((current) => (current?.kind === kind ? null : current)), [set])
}

/**
 * Asks the sidepanel to show a paywall and brings it forward. Writes only
 * GlobalState, so any renderer can call it. {@link useHonorSidepanelPaywallRequest}
 * clears the view stack, because only the sidepanel has the view host.
 */
export function useRequestSidepanelPaywall() {
  const request = useSetAtom(sidepanelPaywallRequestAtom)
  const setSidepanelState = useSetSidepanelState()
  return useCallback(
    (kind: TopUpKind) => {
      request({ kind, requestedAt: Date.now() })
      setSidepanelState('open')
    },
    [request, setSidepanelState],
  )
}

/**
 * Clears the drill-in stack when a new paywall request lands, so the strip on
 * home is what the user sees rather than whatever view was open over it. Lives
 * in the sidepanel because `useViewActions` needs the view host; the requesting
 * window has none.
 */
export function useHonorSidepanelPaywallRequest() {
  const request = useSidepanelPaywallRequest()
  const { closeAllViews } = useViewActions()
  const handled = useRef<number | null>(null)

  useEffect(() => {
    if (!request || handled.current === request.requestedAt) {
      return
    }
    handled.current = request.requestedAt
    closeAllViews()
  }, [request, closeAllViews])
}
